/**
 * Detects if the data is base64 encoded or raw PDF data
 * @param data - The PDF data string
 * @returns true if base64, false if raw PDF
 */
const isBase64Pdf = (data: string): boolean => {
	// Raw PDF data starts with %PDF
	if (data.startsWith("%PDF")) {
		return false;
	}
	// Try to decode as base64 - if it fails, it's likely raw data
	try {
		const decoded = atob(data);
		return decoded.startsWith("%PDF");
	} catch {
		return false;
	}
};

/**
 * Converts base64 PDF data to a Blob
 * @param base64Data - The base64 encoded PDF string
 * @returns Blob containing the PDF data
 */
const base64ToBlob = (base64Data: string): Blob => {
	const byteCharacters = atob(base64Data);
	const byteNumbers: number[] = [];
	for (let i = 0; i < byteCharacters.length; i++) {
		// eslint-disable-next-line unicorn/prefer-code-point -- charCodeAt is correct for byte conversion
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}
	const byteArray = new Uint8Array(byteNumbers);
	return new Blob([byteArray], { type: "application/pdf" });
};

/**
 * Converts raw PDF data to a Blob
 * @param rawData - The raw PDF string
 * @returns Blob containing the PDF data
 */
const rawPdfToBlob = (rawData: string): Blob => {
	const byteNumbers: number[] = [];
	for (let i = 0; i < rawData.length; i++) {
		// eslint-disable-next-line unicorn/prefer-code-point -- charCodeAt is correct for byte conversion
		byteNumbers[i] = rawData.charCodeAt(i);
	}
	const byteArray = new Uint8Array(byteNumbers);
	return new Blob([byteArray], { type: "application/pdf" });
};

/**
 * Converts PDF data (base64 or raw) to a Blob
 * @param pdfData - The PDF data (base64 encoded or raw)
 * @returns Blob containing the PDF data
 */
const pdfDataToBlob = (pdfData: string): Blob => {
	return isBase64Pdf(pdfData) ? base64ToBlob(pdfData) : rawPdfToBlob(pdfData);
};

/**
 * Converts ArrayBuffer PDF data to a Blob
 * @param arrayBuffer - The PDF ArrayBuffer data
 * @returns Blob containing the PDF data
 */
const arrayBufferToBlob = (arrayBuffer: ArrayBuffer): Blob => {
	return new Blob([arrayBuffer], { type: "application/pdf" });
};

/**
 * Utility function to open PDF data in a new browser tab
 * @param pdfData - The PDF data (base64 encoded, raw string, or ArrayBuffer)
 * @param filename - The filename to display in the browser tab
 */
export const openPdfInNewTab = (
	pdfData: string | ArrayBuffer,
	filename: string,
) => {
	try {
		// Convert PDF data to blob (handles string or ArrayBuffer)
		const blob =
			typeof pdfData === "string"
				? pdfDataToBlob(pdfData)
				: arrayBufferToBlob(pdfData);

		// Create a new File object from the blob with the desired filename
		// This preserves the filename when downloading from Chrome's PDF viewer
		const file = new File([blob], filename, { type: "application/pdf" });
		const url = URL.createObjectURL(file);

		// Open in new tab
		const newTab = window.open(url, "_blank");

		if (newTab) {
			newTab.document.title = filename;
		} else {
			console.error("Failed to open new tab - popup blocker may be active");
			// Clean up URL if tab couldn't be opened
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		}

		// Don't revoke the URL immediately - let it persist for the duration of the tab
		// The browser will clean it up when the tab is closed
	} catch (error) {
		console.error("Error opening PDF:", error);
	}
};

/**
 * Utility function to download PDF data as a file
 * @param pdfData - The PDF data (base64 encoded, raw string, or ArrayBuffer)
 * @param filename - The filename for the downloaded file
 */
export const downloadPdf = (
	pdfData: string | ArrayBuffer,
	filename: string,
) => {
	try {
		// Convert PDF data to blob (handles string or ArrayBuffer)
		const blob =
			typeof pdfData === "string"
				? pdfDataToBlob(pdfData)
				: arrayBufferToBlob(pdfData);

		// Create object URL and trigger download
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = filename;

		// Trigger download
		document.body.append(link);
		link.click();
		link.remove();

		// Clean up the object URL
		setTimeout(() => URL.revokeObjectURL(url), 1000);
	} catch (error) {
		console.error("Error downloading PDF:", error);
	}
};
